import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth, useClerk } from "@clerk/react";
import { toast } from "sonner";
import { Download, KeyRound, Trash2 } from "lucide-react";
import { errMsg, fmtDate } from "@/lib/api";
import { useShopperApi } from "@/lib/shopper";
import { useSite } from "@/lib/site";

const field = "w-full h-11 rounded-lg border border-slate-300 px-3 bg-white focus:border-emerald-500 outline-none";
const STATUS = { confirmed: "bg-emerald-100 text-emerald-800", pending: "bg-amber-100 text-amber-800", rejected: "bg-red-100 text-red-800", cancelled: "bg-slate-200 text-slate-700" };

const useProfile = () => {
  const call = useShopperApi();
  const { userId } = useAuth();
  return useQuery({ queryKey: ["shopper-profile", userId], queryFn: async () => (await call("get", "/profile")).data });
};

export const ProfileTab = () => {
  const call = useShopperApi();
  const qc = useQueryClient();
  const { openUserProfile } = useClerk();
  const { data: p } = useProfile();
  const [f, setF] = useState(null);
  useEffect(() => { if (p) setF({ full_name: p.full_name || "", mobile: p.mobile || "", country: p.country || "India", city: p.city || "", marketing_consent: !!p.marketing_consent }); }, [p]);
  if (!f) return <p className="text-slate-500">Loading…</p>;
  const set = (k) => (e) => { const v = e.target.type === "checkbox" ? e.target.checked : e.target.value; setF((x) => ({ ...x, [k]: v })); };
  const save = async (e) => {
    e.preventDefault();
    try { await call("put", "/profile", f); toast.success("Profile saved"); qc.invalidateQueries({ queryKey: ["shopper-profile"] }); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <form onSubmit={save} className="max-w-xl space-y-4" data-testid="profile-form">
      <p className="text-sm text-slate-600">Email: <b data-testid="profile-email">{p.email}</b> {p.email_verified ? <span className="text-emerald-700">(verified)</span> : <span className="text-amber-700">(not verified yet)</span>} · Member since {fmtDate(p.created_at)}</p>
      <label className="block text-sm font-semibold">Full name<input required value={f.full_name} onChange={set("full_name")} className={field} data-testid="profile-name" /></label>
      <label className="block text-sm font-semibold">Mobile number <span className="font-normal text-slate-500">(optional)</span><input value={f.mobile} onChange={set("mobile")} placeholder="+91 …" className={field} data-testid="profile-mobile" /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm font-semibold">Country<input required value={f.country} onChange={set("country")} className={field} data-testid="profile-country" /></label>
        <label className="block text-sm font-semibold">City <span className="font-normal text-slate-500">(optional)</span><input value={f.city} onChange={set("city")} className={field} data-testid="profile-city" /></label>
      </div>
      <label className="flex gap-2 text-sm items-start"><input type="checkbox" checked={f.marketing_consent} onChange={set("marketing_consent")} className="mt-1" data-testid="profile-marketing-consent" />
        <span>Email me hand-picked offers. Optional - you can switch it off any time, and every offer email has an unsubscribe link. Account emails (like password resets) are sent either way.</span></label>
      <div className="flex flex-wrap gap-3">
        <button className="h-11 px-5 rounded-lg bg-emerald-600 text-white font-bold" data-testid="profile-save">Save profile</button>
        <button type="button" onClick={() => openUserProfile()} className="h-11 px-5 rounded-lg border border-slate-300 font-semibold inline-flex items-center gap-2" data-testid="profile-change-password"><KeyRound size={16} /> Password & sign-in</button>
      </div>
    </form>
  );
};

export const ActivityTab = () => {
  const call = useShopperApi();
  const { userId } = useAuth();
  const { data } = useQuery({ queryKey: ["shopper-activity", userId], queryFn: async () => (await call("get", "/activity")).data });
  if (!data) return <p className="text-slate-500">Loading…</p>;
  return (
    <div className="space-y-8" data-testid="activity-tab">
      <section>
        <h3 className="font-bold mb-1">Purchases reported by stores</h3>
        <p className="text-xs text-slate-500 mb-3">Only purchases a store reports back to us appear here, usually days later. Clicking a deal alone is not a purchase.</p>
        {data.transactions.length ? (
          <ul className="divide-y border rounded-xl bg-white">{data.transactions.map((t) => (
            <li key={`${t.merchant}-${t.date}-${t.order_value}`} className="p-3 flex justify-between text-sm"><span>{t.merchant} · {fmtDate(t.date)}{t.description && ` · ${t.description}`}</span>
              <span>{t.order_value ? `₹${t.order_value}` : ""} <b className={`ml-2 px-2 py-0.5 rounded-full text-xs ${STATUS[t.status]}`}>{t.status}</b></span></li>))}</ul>
        ) : <p className="text-sm text-slate-600" data-testid="activity-no-purchases">No purchases reported yet.</p>}
      </section>
      <section>
        <h3 className="font-bold mb-3">Deals you opened</h3>
        {data.clicks.length ? (
          <ul className="divide-y border rounded-xl bg-white text-sm">{data.clicks.map((c) => <li key={`${c.ts}-${c.title}`} className="p-3 flex justify-between"><span>{c.title}</span><span className="text-slate-500">{c.merchant_name} · {fmtDate(c.ts)}</span></li>)}</ul>
        ) : <p className="text-sm text-slate-600" data-testid="activity-no-clicks">No deal clicks yet.</p>}
      </section>
    </div>
  );
};

export const PrivacyTab = () => {
  const siteName = useSite().name;
  const call = useShopperApi();
  const qc = useQueryClient();
  const { data: p } = useProfile();
  const download = async () => {
    try {
      const blob = new Blob([JSON.stringify((await call("get", "/export")).data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "bachatraja-my-data.json";
      a.click();
    } catch (er) { toast.error(errMsg(er)); }
  };
  const del = async () => {
    if (!window.confirm(`Ask us to delete your ${siteName} account and personal data? This can't be undone once approved.`)) return;
    try { toast.success((await call("post", "/deletion-request")).data.message, { duration: 9000 }); qc.invalidateQueries({ queryKey: ["shopper-profile"] }); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <div className="max-w-xl space-y-5 text-sm" data-testid="privacy-tab">
      <p>We keep your profile, saved deals, the deals you open and purchases stores report to us - only to run your account and show your activity. We never ask for card details or store passwords, and we never send your name, email or phone to stores or affiliate networks. <a href="/privacy-policy" className="text-emerald-700 font-semibold">Privacy policy</a></p>
      <button onClick={download} className="h-11 px-5 rounded-lg border border-slate-300 font-semibold inline-flex items-center gap-2" data-testid="privacy-download"><Download size={16} /> Download my data</button>
      <div className="border-t pt-5">
        {p?.pending_deletion ? <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="privacy-deletion-pending">Your deletion request is with us. An administrator will complete it within 30 days.</p> : (
          <button onClick={del} className="h-11 px-5 rounded-lg border border-red-300 text-red-700 font-semibold inline-flex items-center gap-2" data-testid="privacy-delete-request"><Trash2 size={16} /> Request account deletion</button>
        )}
      </div>
    </div>
  );
};
